import {
  alpha,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useMemo, useState } from 'react';
import { BuildingAlternative } from '../../stores/SubprojectStore.ts';
import { SearchBox } from '../components/TechToolbar';
import {
  ElectricRateCard,
  EnergyMinMax,
  EnergyRateCard,
  ExternalEnergy,
  GasRateCard,
  OnsiteEnergy,
} from '../types';
import { EnergyUnit } from './EnergyUnit.tsx';
import {
  adaptElectric,
  adaptGas,
  filterEnergyUnits,
  isExternalEnergy,
  normalizeEnergyUnit,
} from './energyUtils';

interface EnergyAvailableListingProps {
  onsite: OnsiteEnergy[];
  electricRates: ElectricRateCard[];
  gasRates: GasRateCard[];
  alternative: BuildingAlternative;
  minMax: EnergyMinMax;
  totalElectricityUsePerYear: number;
  onAddOnsiteEnergy: (techId: string) => void;
  onAddExternalEnergy: (card: EnergyRateCard) => void;
}

export const EnergyAvailableListing: React.FC<EnergyAvailableListingProps> = ({
  onsite,
  electricRates,
  gasRates,
  alternative,
  minMax,
  totalElectricityUsePerYear,
  onAddOnsiteEnergy,
  onAddExternalEnergy,
}) => {
  const theme = useTheme();
  const [energyType, setEnergyType] = useState<'internal' | 'external'>(
    'internal',
  );
  const [searchString, setSearchString] = useState('');

  // Reset search state when energy type toggle changes
  React.useEffect(() => {
    setSearchString('');
  }, [energyType]);

  // Energy type filter for onsite (internal) only
  const [energyFilter] = useState<string[]>([]);

  const excludedOnsiteIds = useMemo(
    () =>
      new Set(
        (alternative?.onSiteEnergy?.params || []).map((p) => String(p.techId)),
      ),
    [alternative],
  );

  // Build excluded set using natural keys
  const excludedExternalKeys = useMemo(
    () =>
      new Set(
        (alternative?.externalEnergy?.params || []).map(
          (p) => `${p.utilityIdentifier}:${p.ratePlanType}:${p.state}:${p.sector}`,
        ),
      ),
    [alternative],
  );

  // Adapt rate cards to ExternalEnergy for use with EnergyUnit
  const adaptedElectric = useMemo<ExternalEnergy[]>(
    () => electricRates.map(adaptElectric),
    [electricRates],
  );
  const adaptedGas = useMemo<ExternalEnergy[]>(
    () => gasRates.map(adaptGas),
    [gasRates],
  );

  // Lookup map: natural key → original EnergyRateCard (for onAdd callback)
  const rateCardByKey = useMemo<Map<string, EnergyRateCard>>(() => {
    const map = new Map<string, EnergyRateCard>();
    electricRates.forEach((c) => map.set(`${c.utilityIdentifier}:electric:${c.state}:${c.sector}`, { ...c, ratePlanType: 'electric' } as EnergyRateCard));
    gasRates.forEach((c) => map.set(`${c.utilityIdentifier}:gas:${c.state}:${c.sector}`, { ...c, ratePlanType: 'gas' } as EnergyRateCard));
    return map;
  }, [electricRates, gasRates]);

  const filteredElectric = useMemo(
    () =>
      adaptedElectric.filter((u) => {
        if (excludedExternalKeys.has(u.id)) return false;
        if (searchString && !u.name.toLowerCase().includes(searchString.toLowerCase())) return false;
        return true;
      }),
    [adaptedElectric, excludedExternalKeys, searchString],
  );

  const filteredGas = useMemo(
    () =>
      adaptedGas.filter((u) => {
        if (excludedExternalKeys.has(u.id)) return false;
        if (searchString && !u.name.toLowerCase().includes(searchString.toLowerCase())) return false;
        return true;
      }),
    [adaptedGas, excludedExternalKeys, searchString],
  );

  return (
    <Box
      sx={{
        gridArea: 'listing',
        gap: 2,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <ToggleButtonGroup
          value={energyType}
          exclusive
          onChange={(_, newValue) => {
            if (newValue !== null) {
              setEnergyType(newValue);
            }
          }}
          size={'medium'}
        >
          <ToggleButton
            value='internal'
            sx={{
              backgroundColor:
                energyType === 'internal'
                  ? 'secondary.main'
                  : 'background.card',
              color: energyType === 'internal' ? 'info.dark' : 'secondary.main',
              '&:hover': {
                backgroundColor:
                  energyType === 'internal'
                    ? alpha(theme.palette.secondary.main, 0.9)
                    : alpha(theme.palette.background.card!, 0.8),
              },
              '&.Mui-selected': {
                backgroundColor: 'secondary.main',
                color: 'info.dark',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                },
              },
            }}
          >
            Internal Production
          </ToggleButton>
          <ToggleButton
            value='external'
            sx={{
              backgroundColor:
                energyType === 'external'
                  ? 'secondary.main'
                  : 'background.card',
              color: energyType === 'external' ? 'info.dark' : 'secondary.main',
              '&:hover': {
                backgroundColor:
                  energyType === 'external'
                    ? alpha(theme.palette.secondary.main, 0.9)
                    : alpha(theme.palette.background.card!, 0.8),
              },
              '&.Mui-selected': {
                backgroundColor: 'secondary.main',
                color: 'info.dark',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                },
              },
            }}
          >
            External Sources
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: { xs: 20, sm: 24, md: 28, lg: 32, xl: 36 },
        }}
      >
        <SearchBox query={searchString} setQuery={setSearchString} />
      </Box>
      <Box
        sx={{
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          height: 'stretch',
          pb: 4,
          pr: 2,
        }}
      >
        {energyType === 'internal' ? (
          <Grid container rowSpacing={3} columnSpacing={1.5}>
            {filterEnergyUnits(onsite, {
              excludeIds: excludedOnsiteIds,
              searchString,
              energyFilter: energyFilter as never[],
            }).map((unit) => {
              const { normalizedEnergy, normalizedCapital, limit } =
                normalizeEnergyUnit(unit, minMax, totalElectricityUsePerYear, alternative);

              return (
                <Grid size={{ xs: 6, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={unit.id + unit.name}>
                  <EnergyUnit
                    unit={unit}
                    normalizedEnergy={normalizedEnergy}
                    normalizedCapital={normalizedCapital}
                    onAdd={() => {
                      if (!isExternalEnergy(unit)) {
                        onAddOnsiteEnergy(String(unit.id));
                      }
                    }}
                    onChange={console.log}
                    onDecrease={console.log}
                    onIncrease={console.log}
                    totalCount={0}
                    limit={limit}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <>
            {/* Electric Providers */}
            <Box>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Electric Providers
              </Typography>
              {adaptedElectric.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  No electric rate data for this location.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {filteredElectric.map((unit) => {
                    const { normalizedEnergy, normalizedCapital } =
                      normalizeEnergyUnit(unit, minMax, totalElectricityUsePerYear, alternative);
                    return (
                      <Grid size={{ xs: 6, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={unit.id}>
                        <EnergyUnit
                          unit={unit}
                          normalizedEnergy={normalizedEnergy}
                          normalizedCapital={normalizedCapital}
                          onAdd={(id) => {
                            const card = rateCardByKey.get(id);
                            if (card) onAddExternalEnergy(card);
                          }}
                          onChange={console.log}
                          onDecrease={console.log}
                          onIncrease={console.log}
                          totalCount={0}
                          limit={0}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>

            {/* Gas Providers */}
            <Box>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Gas Providers
              </Typography>
              {adaptedGas.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  No gas rate data for this location.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {filteredGas.map((unit) => {
                    const { normalizedEnergy, normalizedCapital } =
                      normalizeEnergyUnit(unit, minMax, totalElectricityUsePerYear, alternative);
                    return (
                      <Grid size={{ xs: 6, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={unit.id}>
                        <EnergyUnit
                          unit={unit}
                          normalizedEnergy={normalizedEnergy}
                          normalizedCapital={normalizedCapital}
                          onAdd={(id) => {
                            const card = rateCardByKey.get(id);
                            if (card) onAddExternalEnergy(card);
                          }}
                          onChange={console.log}
                          onDecrease={console.log}
                          onIncrease={console.log}
                          totalCount={0}
                          limit={0}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
